/**
 * "How priorities are set": the methodology panel PRD §4.1 requires. It states plainly that the score
 * is calibrated against which contacts advisors chose to invite, not against verified long-term care
 * need, and describes the priorities, the personas contacts are compared with, and whether the model
 * has been approved.
 */
import { Badge, Button, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@design-system';
import { CircleHelp } from 'lucide-react';
import type { Prospecting } from '@client/api/types';
import { crmCopy } from '../lib/crm-copy';
import { priorityCopy } from '../lib/labels';
import { PriorityBadge } from './priority-badge';

const PRIORITIES = ['tier1', 'tier2', 'tier3'] as const;

interface MethodologySheetProps {
  book: Prospecting.BookStatus;
}

export function MethodologySheet({ book }: MethodologySheetProps) {
  const copy = crmCopy(book.provider);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          <CircleHelp />
          {priorityCopy.howItWorks}
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col gap-6 overflow-y-auto pb-16 sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{priorityCopy.howItWorks}</SheetTitle>
          <SheetDescription>{priorityCopy.howItWorksIntro(copy.name)}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-5 px-4 text-sm text-gray-700">
          <section className="flex flex-col gap-1">
            <h3 className="font-semibold text-gray-900">Fit</h3>
            <p>
              Each contact is compared with groups of people who completed a Waterlily plan, using age, gender, marital
              status, spouse&apos;s age, state and household net worth where {copy.name} records them.
            </p>
            <p className="rounded-md bg-muted p-3">
              These groups reflect who advisors chose to invite to Waterlily, not who has a verified need for
              long-term care or who acted on a plan. The ranking will repeat the habits behind past invitations.
            </p>
          </section>

          <section className="flex flex-col gap-1">
            <h3 className="font-semibold text-gray-900">{priorityCopy.sectionTitle}</h3>
            <ul className="flex flex-col gap-3">
              {PRIORITIES.map(tier => (
                <li key={tier} className="flex flex-col gap-1">
                  <PriorityBadge tier={tier} />
                  <span>{priorityCopy.rule(tier, copy.parentRecorded)}</span>
                  <span className="text-gray-900">What to do: {book.tierActions[tier]}.</span>
                </li>
              ))}
              <li className="flex flex-col gap-1">
                <PriorityBadge tier="untiered" />
                <span>{priorityCopy.lowFitRule}</span>
              </li>
            </ul>
            <p>{priorityCopy.noCareNeed}</p>
          </section>

          <section className="flex flex-col gap-1">
            <h3 className="font-semibold text-gray-900">Groups compared with</h3>
            <ul className="list-disc pl-4">
              {book.scoring.personas.map(persona => (
                <li key={persona.id}>{persona.summary}</li>
              ))}
            </ul>
          </section>

          <section className="flex items-center gap-2">
            <span>Model {book.scoring.modelVersion}</span>
            {book.scoring.modelSignedOff ? (
              <Badge variant="success">Approved</Badge>
            ) : (
              <Badge variant="warning">Preview: not approved</Badge>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
